import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

// Smooth Scroll
const lenis = new Lenis();

lenis.on("scroll", ScrollTrigger.update);

gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
});

gsap.ticker.lagSmoothing(0);

// Cursor Follow
const parents = document.querySelectorAll(".cursor-parent");

parents?.forEach((parent) => {
    const follower = parent.querySelector(".cursor-follow");
    if (!follower) return;

    let pos = { x: parent.offsetWidth / 2, y: parent.offsetHeight / 2 };
    let mouse = { ...pos };
    const speed = 0.2;
    const fpms = 60 / 1000;

    parent.addEventListener("pointermove", (e) => {
        const rect = parent.getBoundingClientRect();
        mouse.x = e.clientX - rect.left;
        mouse.y = e.clientY - rect.top;
    });

    const xSet = gsap.quickSetter(follower, "x", "px");
    const ySet = gsap.quickSetter(follower, "y", "px");

    const followerOffset = {
        x: follower.offsetWidth / 2,
        y: follower.offsetHeight / 2,
    };

    gsap.ticker.add((time, deltaTime) => {
        let delta = deltaTime * fpms;
        let dt = 1.0 - Math.pow(1.0 - speed, delta);

        pos.x += (mouse.x - pos.x) * dt;
        pos.y += (mouse.y - pos.y) * dt;

        xSet(pos.x - followerOffset.x); // center on mouse
        ySet(pos.y - followerOffset.y);
    });
});

// Hero blur-in (on page load for above-the-fold banners)
const blurIn = document.querySelectorAll("[data-blur-in]");
blurIn?.forEach((item) => {
    gsap.from(item, {
        filter: "blur(20px)",
        opacity: 0,
        duration: 1.8,
        delay: 0.1,
        ease: "expo.out",
        force3D: true,
        overwrite: "auto",
        onStart: () => item.classList.add("is-blur-animating"),
        onComplete: () => item.classList.remove("is-blur-animating"),
    });
});

// Image Load Animation
const reveal = document.querySelectorAll("[data-reveal]");
reveal?.forEach((item, index) => {
    const iframe = item.querySelector("iframe");

    const animate = (el, delay = 0) => {
        gsap.from(el, {
            filter: "blur(12px)",
            opacity: 0,
            duration: 1,
            delay: delay,
            scrollTrigger: {
                trigger: item,
                start: "top+=100px bottom",
            },
        });
    };

    if (iframe) {
        animate(item)
        // Vimeo.Player instantiation removed - was unused (callback commented out)
        // and caused "Vimeo is not defined" when script load order varied
    } else {
        animate(item)
    }
});

// Image Parallax
// const parallax = document.querySelectorAll(".parallax");
// parallax?.forEach((item) => {
//     gsap.set(item, { scale: 1.15, transformOrigin: "bottom" });
//     gsap.to(item, {
//         yPercent: 15,
//         ease: "power1.inOut",
//         immediateRender: false,
//         scrollTrigger: {
//             trigger: item,
//             start: "top-=400px center",
//             end: "bottom+=400px center",
//             scrub: 1,
//         },
//     });
// });

// Text Reveal
function createRevealSpan(content, wordsRefs, isHtml = false) {
    const span = document.createElement("span");
    span.className =
        "inline-flex relative overflow-hidden pb-[0.3ch] -mb-[0.3ch]";
    const inner = document.createElement("span");
    inner.className = "inner-flex";
    if (isHtml) {
        inner.innerHTML = content;
    } else {
        inner.textContent = content;
    }
    wordsRefs.push(inner);
    span.append(inner);
    return span;
}

const textReveal = document.querySelectorAll("[data-text-reveal]");
textReveal?.forEach((el) => {
    let html = el.innerHTML.trim();
    // Normalize rich text: convert block tags (p, div, etc.) to <br> for line splitting
    html = html.replace(/<\/(p|div|h[1-6])>\s*<\1[\s>]/gi, "<br>");
    const lineHtmls = html
        .split(/<br\s*\/?>/gi)
        .filter((l) => l.trim().length > 0);
    el.innerHTML = "";
    el.classList.add("flex", "flex-wrap", "gap-y-[0.1ch]", "gap-x-[0.2ch]");

    const wordsRefs = [];

    lineHtmls.forEach((lineHtml, lineIndex) => {
        if (lineIndex > 0) {
            const br = document.createElement("span");
            br.className = "w-full";
            el.append(br);
        }

        const lineDiv = document.createElement("div");
        lineDiv.innerHTML = lineHtml.trim();

        const inlineTags = ["A", "STRONG", "EM", "B", "I", "U", "MARK"];
        const blockTags = ["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6"];
        let isFirst = true;

        function processNodes(nodes) {
            nodes.forEach((node) => {
                const appendSegment = (revealSpan) => {
                    if (!isFirst) el.append(document.createTextNode(" "));
                    el.append(revealSpan);
                    isFirst = false;
                };
                if (node.nodeType === Node.TEXT_NODE) {
                    const words = node.textContent
                        .trim()
                        .split(/\s+/)
                        .filter((w) => w.length > 0);
                    words.forEach((word) => {
                        appendSegment(createRevealSpan(word, wordsRefs));
                    });
                } else if (
                    node.nodeType === Node.ELEMENT_NODE &&
                    inlineTags.includes(node.tagName)
                ) {
                    appendSegment(
                        createRevealSpan(node.outerHTML, wordsRefs, true),
                    );
                } else if (
                    node.nodeType === Node.ELEMENT_NODE &&
                    blockTags.includes(node.tagName)
                ) {
                    processNodes(node.childNodes);
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const text = node.textContent
                        .trim()
                        .split(/\s+/)
                        .filter((w) => w.length > 0);
                    text.forEach((word) => {
                        appendSegment(createRevealSpan(word, wordsRefs));
                    });
                }
            });
        }

        processNodes(lineDiv.childNodes);
    });

    gsap.from(wordsRefs, {
        yPercent: 140,
        duration: 0.5,
        stagger: 0.05,
        ease: "cubic-bezier(0.625, 0.05, 0, 1)",
        scrollTrigger: {
            trigger: el,
            toggleActions: "play none none reverse",
        },
    });
});

// Feed card parallax
const $feedGrids = document.querySelectorAll("[data-feed-parallax]");
const $feedGridsType3 = document.querySelectorAll(
    "[data-feed-parallax-type-3]",
);
const mm = gsap.matchMedia();

function initFeedParallax($els, offsets) {
    $els.forEach((grid) => {
        const cards = Array.from(grid.children);
        cards.forEach((card, index) => {
            const offset = offsets[index % offsets.length];
            if (offset === 0) return;

            gsap.fromTo(
                card,
                {
                    y: offset,
                },
                {
                    y: 0,
                    ease: "none",
                    scrollTrigger: {
                        trigger: card,
                        start: "top bottom",
                        end: "center center",
                        scrub: 2,
                        invalidateOnRefresh: true,
                    },
                },
            );
        });
    });
}

mm.add("(min-width: 1440px)", () => {
    initFeedParallax($feedGridsType3, [0, 30, 15, 25, 10, 20]);
});

mm.add("(min-width: 1280px)", () => {
    initFeedParallax($feedGrids, [0, 60, 25, 25, 20, 40]);
});

mm.add("(max-width: 1279px)", () => {
    initFeedParallax($feedGrids, [0, 30, 15, 25, 15, 20]);
});

// Profile Subtle movement
const profiles = document.querySelectorAll(".profile");

mm.add("(min-width: 1280px)", () => {
    profiles?.forEach((profile) => {
        // Find the theTeam section containing this profile
        const teamSection = profile.closest("section");
        const footer = document.querySelector("footer");

        // Only animate if footer exists and team section is before footer
        if (
            teamSection &&
            footer &&
            teamSection.compareDocumentPosition(footer) &
                Node.DOCUMENT_POSITION_FOLLOWING
        ) {
            gsap.to(profile, {
                translateY: 0,
                scrollTrigger: {
                    trigger: profile,
                    start: "top bottom",
                    end: "bottom bottom",
                    scrub: 2,
                    invalidateOnRefresh: true,
                    refreshPriority: -1,
                },
            });
        }
    });
});
